
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const TermsOfService = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-24">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-8 text-earth-800">Terms of Service</h1>
          <div className="prose prose-earth max-w-none">
            <div className="space-y-6 text-earth-600">
              <p>Last updated: September 14, 2025</p>
              
              <p className="text-lg text-earth-600 mb-6">
                Welcome to Resonow ("Resonow," "we," "our," "us"). These Terms of Service ("Terms") govern your access to and use of the Resonow platform, including our website, mobile applications, and related services (collectively, the "Platform"). Please read these Terms carefully. By accessing or using the Platform, you agree to be bound by these Terms. If you do not agree, do not use the Platform.
              </p>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">1. Platform Role and Services</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Intermediary Role:</strong> Resonow provides an online platform that connects clients ("Clients") with independent practitioners ("Practitioners") who offer healing, wellness, and related services.</li>
                  <li><strong>Independent Practitioners:</strong> Practitioners are independent contractors, not employees or agents of Resonow. We do not control or guarantee the quality, accuracy, or outcomes of services.</li>
                  <li><strong>Non-Medical Advice:</strong> Services delivered via the Platform are for wellness support and are not intended to substitute professional medical advice, diagnosis, or treatment. Always seek the advice of a qualified healthcare provider for medical concerns.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">2. User Accounts and Responsibilities</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account Creation:</strong> Users must provide accurate, current, and complete information during registration and keep their credentials secure.</li>
                  <li><strong>Account Security:</strong> You are responsible for all activities under your account. Notify us immediately at support@resonow.com if you suspect unauthorized use.</li>
                  <li><strong>Eligibility:</strong> You must be at least 18 years old (or the legal age in your jurisdiction) to use the Platform.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">3. Booking, Payments, and Fees</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Booking:</strong> Clients may book sessions with Practitioners through the Platform.</li>
                  <li><strong>Payments:</strong> All payments are processed securely by third-party payment provider Stripe. Resonow does not store full payment card information.</li>
                  <li><strong>Fees:</strong> Resonow collects service fees from transactions and remits the balance to Practitioners.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">4. Cancellations and Refunds</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Client Cancellation:</strong> Clients may cancel bookings before the session starts. Late cancellations may require agreement with the Practitioner.</li>
                  <li><strong>Practitioner Cancellation:</strong> Practitioners who cancel are responsible for notifying Clients.</li>
                  <li><strong>Refunds:</strong> Refunds are assessed case by case. Requests must be submitted through designated channels with supporting details. Resonow reserves final discretion over refunds.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">5. Use of Google Services</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Google Meet Integration:</strong> Practitioners may connect their Google accounts via OAuth to schedule sessions. We access only the minimum data necessary (tokens, calendar event metadata, meeting links) to create and manage sessions.</li>
                  <li><strong>Data Handling:</strong> OAuth tokens are encrypted, stored securely, and used only by our server-side processes. We do not share or sell Google user data.</li>
                  <li><strong>Revocation:</strong> Practitioners can disconnect Google at any time, which revokes our access.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">6. Data Protection and Privacy</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Privacy Policy:</strong> Your use of the Platform is also governed by our Privacy Policy, which describes how we collect, use, and protect your information.</li>
                  <li><strong>Security Measures:</strong> We implement industry-standard protections, including encryption (TLS/HTTPS, AES-256), role-based access controls, audit logging, and regular security testing.</li>
                  <li><strong>Compliance:</strong> We align our practices with GDPR, CCPA, and other applicable data protection regulations.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">7. User Conduct and Prohibited Uses</h2>
                <p className="mb-4">You agree not to use the Platform to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Violate any laws or regulations.</li>
                  <li>Misrepresent your identity or qualifications.</li>
                  <li>Harass, abuse, or harm other users.</li>
                  <li>Upload malicious code, attempt unauthorized access, or disrupt system functionality.</li>
                  <li>Reverse-engineer, copy, or misuse the Platform for competitive purposes.</li>
                </ul>
                <p className="mt-4">Resonow may suspend or terminate accounts for violations of these Terms.</p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">8. Intellectual Property</h2>
                <p>All content, trademarks, logos, and software on the Platform are the property of Resonow or its licensors. You may not copy, modify, or distribute materials without prior written permission.</p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">9. Service Modifications and Termination</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>We may update, suspend, or discontinue parts of the Platform at any time, with reasonable notice where practical.</li>
                  <li>We reserve the right to terminate or suspend user accounts for breach of these Terms or misuse of the Platform.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">10. Disclaimers and Limitation of Liability</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The Platform and services are provided "as is" and "as available."</li>
                  <li>Resonow does not guarantee uninterrupted access or error-free operation.</li>
                  <li>To the maximum extent permitted by law, Resonow disclaims liability for indirect, incidental, or consequential damages arising from your use of the Platform.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">11. Governing Law and Dispute Resolution</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>These Terms shall be governed by and construed in accordance with the laws of the State of New York, United States, without regard to conflict of law principles.</li>
                  <li>Any disputes shall be resolved in the competent courts located in New York, unless applicable law requires otherwise.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">12. Changes to Terms</h2>
                <p>We may update these Terms from time to time. Material changes will be communicated by notice on the Platform or by email. Continued use of the Platform after updates constitutes acceptance of the revised Terms.</p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">13. Contact Us</h2>
                <p>For questions about these Terms, contact us at:</p>
                <p>📧 info@resonow.org</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsOfService;
